import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  Agent,
  AGENT_PERMISSIONS,
  AGENT_STATUSES,
  AgentIdentity,
  AgentRequest,
  AgentStatus,
  agentSaveErrorMessage,
  avatarFileProblem,
  AVATAR_TYPES,
  emptyIdentity,
  REASONING_EFFORT_LABELS,
  IDENTITY_SECTIONS,
  LlmModel,
  ProjectOption,
  ProjectPlan,
  Provider,
  REPLY_DELAY_DEFAULT_MS,
  REPLY_DELAY_MAX_MS,
  withPlanDefaults,
} from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';
import { blankToNull, numOrNull } from '../../utils/clipboard';

export type EditorTab = 'settings' | 'identity' | 'channels' | 'playbooks' | 'tools' | 'automations' | 'test';

interface SettingsForm {
  projectId: string;
  name: string;
  avatarUrl: string;
  modelId: string;
  temperature: number | null;
  maxOutputTokens: number | null;
  historyLimit: number | null;
  status: AgentStatus;
  welcomeMessage: string;
  fallbackMessage: string;
  /** '' = model default */
  reasoningEffort: string;
  /** Contract v1.2 options: messages in a row, images, voice, knowledge files. */
  replyDelayMs: number | null;
  imageInputEnabled: boolean;
  voiceInputEnabled: boolean;
  voiceOutputEnabled: boolean;
  /** '' = the default voice of the SPEECH model */
  voiceName: string;
  knowledgeFilesEnabled: boolean;
  documentInputEnabled: boolean;
}

/** null when the reply delay is a whole number from 0 to 10000 ms, else the error text. */
export function replyDelayProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > REPLY_DELAY_MAX_MS) {
    return 'Reply delay must be a whole number from 0 to ' + REPLY_DELAY_MAX_MS + ' ms.';
  }
  return null;
}

interface ModelGroup {
  label: string;
  items: { label: string; value: string; disabled?: boolean }[];
}

/** Grouped options for the model picker: enabled models by provider, plus the current model if disabled. */
export function buildModelGroups(models: LlmModel[], providers: Provider[], currentModelId?: string | null): ModelGroup[] {
  const groups = new Map<string, ModelGroup>();
  const providerLabel = (id: string) => {
    const p = providers.find((x) => x.id === id);
    return p ? `${p.displayName}${p.enabled ? '' : ' (provider disabled)'}` : id;
  };
  for (const m of models) {
    const isCurrent = m.id === currentModelId;
    if (!m.enabled && !isCurrent) continue;
    let g = groups.get(m.providerId);
    if (!g) {
      g = { label: providerLabel(m.providerId), items: [] };
      groups.set(m.providerId, g);
    }
    g.items.push({
      label: `${m.displayName} (${m.modelId})${m.enabled ? '' : ' - disabled'}`,
      value: m.id,
    });
  }
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

@Component({
  selector: 'app-ai-agent-editor-page',
  templateUrl: './agent-editor-page.component.html',
  styleUrls: ['../../ai-agents.shared.css', './agent-editor-page.component.css'],
})
export class AgentEditorPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  readonly statuses = AGENT_STATUSES;
  readonly sections = IDENTITY_SECTIONS;

  agentId: string | null = null;
  agent: Agent | null = null;
  tab: EditorTab = 'settings';

  projects: ProjectOption[] = [];
  models: LlmModel[] = [];
  providers: Provider[] = [];
  modelGroups: ModelGroup[] = [];

  form: SettingsForm = this.blankForm();
  identity: AgentIdentity = emptyIdentity();
  error = '';
  identityError = '';
  savedNote = '';
  avatarError = '';
  avatarBusy = false;
  /** Voices of the default SPEECH capability model; empty = free text. */
  voices: string[] = [];
  defaultVoice = '';
  /** The plan of the agent's project, to show which features it allows; null = unknown. */
  plan: ProjectPlan | null = null;
  readonly replyDelayMax = REPLY_DELAY_MAX_MS;
  readonly avatarAccept = '.png,.jpg,.jpeg,.webp,' + AVATAR_TYPES.join(',');

  constructor(
    private api: AiAgentsApiService,
    public perms: AgentPermissionsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  get isNew(): boolean {
    return !this.agentId;
  }

  get canEdit(): boolean {
    return this.isNew ? this.perms.has(this.P.agentCreate) : this.perms.has(this.P.agentUpdate);
  }

  get selectedModel(): LlmModel | undefined {
    return this.models.find((m) => m.id === this.form.modelId);
  }

  /** The saved model of the agent, for the Playbooks tab (contract v1.4: supportsTools). */
  get agentModel(): LlmModel | undefined {
    return this.agent ? this.models.find((m) => m.id === this.agent?.modelId) : undefined;
  }

  /** Thinking levels of the chosen model; empty = the field is hidden. */
  get thinkingLevels(): { value: string; label: string }[] {
    return (this.selectedModel?.reasoningEfforts || []).map((v) => ({ value: v, label: REASONING_EFFORT_LABELS[v] || v }));
  }

  get thinkingDefaultLabel(): string {
    const d = this.selectedModel?.defaultReasoningEffort;
    return d ? `Model default (${REASONING_EFFORT_LABELS[d] || d})` : 'Model default';
  }

  /** A switched-on feature the plan does not allow (the service answers 409 FEATURE_NOT_IN_PLAN). */
  notInPlan(feature: 'allowImages' | 'allowDocuments' | 'allowVoiceInput' | 'allowVoiceOutput' | 'allowKnowledgeFiles' | 'allowPlaybooks'): boolean {
    return !!this.plan && this.plan[feature] !== true;
  }

  private loadPlan(projectId: string | null | undefined): void {
    this.plan = null;
    if (!projectId || !this.perms.has(this.P.planGet)) return;
    this.api
      .getProjectPlan(projectId)
      .pipe(catchError(() => of(null)))
      .subscribe((p) => (this.plan = p ? withPlanDefaults(p) : null));
  }

  private loadVoices(): void {
    if (!this.perms.has(this.P.capabilityList)) return;
    this.api
      .listCapabilityModels('SPEECH')
      .pipe(catchError(() => of([])))
      .subscribe((list) => {
        const speech = list.find((m) => m.isDefault && m.enabled) || list.find((m) => m.enabled);
        const voices = speech?.options?.voices;
        this.voices = Array.isArray(voices) ? voices.filter((v) => typeof v === 'string') : [];
        this.defaultVoice = (speech?.options?.defaultVoice as string) || '';
      });
  }

  onModelChange(modelId: string): void {
    this.form.modelId = modelId;
    if (this.form.reasoningEffort && !(this.selectedModel?.reasoningEfforts || []).includes(this.form.reasoningEffort)) {
      this.form.reasoningEffort = '';
    }
  }

  // ------------------------------------------------------------ picture (support uploads for the customer)

  onAvatarPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file || !this.agentId) return;
    this.avatarError = '';
    const problem = avatarFileProblem(file);
    if (problem) {
      this.avatarError = problem === 'size' ? 'The picture is larger than 2 MB.' : problem === 'type' ? 'Use a PNG, JPEG or WebP picture.' : 'The file is empty.';
      return;
    }
    this.avatarBusy = true;
    this.api.uploadAvatar(this.agentId, file).subscribe({
      next: (a) => this.avatarDone(a),
      error: (err) => this.avatarFailed(err, 'Uploading the picture failed.'),
    });
  }

  removeAvatar(): void {
    if (!this.agentId) return;
    this.avatarError = '';
    this.avatarBusy = true;
    this.api.removeAvatar(this.agentId).subscribe({
      next: (a) => this.avatarDone(a),
      error: (err) => this.avatarFailed(err, 'Removing the picture failed.'),
    });
  }

  private avatarDone(a: Agent): void {
    this.avatarBusy = false;
    if (this.agent) this.agent = { ...this.agent, avatarUrl: a.avatarUrl ?? null, updatedAt: a.updatedAt };
    this.form.avatarUrl = a.avatarUrl || '';
  }

  private avatarFailed(err: any, fallback: string): void {
    this.avatarBusy = false;
    this.avatarError = err?.error?.message || fallback;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.agentId = id && id !== 'new' ? id : null;
      this.init();
    });
    this.route.queryParamMap.subscribe((q) => {
      const t = q.get('tab') as EditorTab | null;
      this.tab = t && ['settings', 'identity', 'channels', 'playbooks', 'tools', 'automations', 'test'].includes(t) ? t : 'settings';
      if (this.isNew && (this.tab === 'channels' || this.tab === 'playbooks' || this.tab === 'tools' || this.tab === 'automations' || this.tab === 'test')) {
        this.tab = 'settings';
      }
      if (this.tab === 'playbooks' && !this.perms.has(this.P.playbookList)) {
        this.tab = 'settings';
      }
    });
  }

  private init(): void {
    this.error = '';
    this.savedNote = '';
    this.api.listProjects().pipe(catchError(() => of([]))).subscribe((list) => (this.projects = list));
    this.loadVoices();

    const models$ = this.perms.has(this.P.modelList) ? this.api.listModels().pipe(catchError(() => of([]))) : of([]);
    const providers$ = this.perms.has(this.P.providerList)
      ? this.api.listProviders().pipe(catchError(() => of([])))
      : of([]);

    if (this.isNew) {
      this.agent = null;
      this.form = this.blankForm();
      this.form.projectId = this.route.snapshot.queryParamMap.get('projectId') || '';
      this.identity = emptyIdentity();
      this.loadPlan(this.form.projectId);
      forkJoin([models$, providers$]).subscribe(([models, providers]) => {
        this.models = models;
        this.providers = providers;
        this.modelGroups = buildModelGroups(models, providers, null);
      });
      return;
    }

    const id = this.agentId as string;
    forkJoin([
      this.api.getAgent(id),
      this.api.getIdentity(id).pipe(catchError(() => of(emptyIdentity()))),
      models$,
      providers$,
    ]).subscribe({
      next: ([agent, identity, models, providers]) => {
        this.agent = agent;
        this.models = models;
        this.providers = providers;
        this.form = this.toForm(agent);
        this.identity = { ...emptyIdentity(), ...(identity || agent.identity || {}) };
        this.modelGroups = buildModelGroups(models, providers, agent.modelId);
        this.loadPlan(agent.projectId);
      },
      error: () => (this.error = 'The agent could not be loaded.'),
    });
  }

  setTab(tab: EditorTab): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { tab }, replaceUrl: true });
  }

  back(): void {
    this.router.navigate(['/ai-agents/agents'], {
      queryParams: { projectId: this.agent?.projectId || this.form.projectId || null },
    });
  }

  saveSettings(): void {
    this.error = '';
    this.savedNote = '';
    const f = this.form;
    const name = blankToNull(f.name);
    const modelId = blankToNull(f.modelId);
    const temperature = numOrNull(f.temperature);
    if (!name || !modelId) {
      this.error = 'Name and model are required.';
      return;
    }
    if (temperature !== null && (temperature < 0 || temperature > 2)) {
      this.error = 'Temperature must be between 0 and 2.';
      return;
    }
    const delayProblem = replyDelayProblem(f.replyDelayMs);
    if (delayProblem) {
      this.error = delayProblem;
      return;
    }
    const features = {
      replyDelayMs: numOrNull(f.replyDelayMs),
      imageInputEnabled: f.imageInputEnabled,
      voiceInputEnabled: f.voiceInputEnabled,
      voiceOutputEnabled: f.voiceOutputEnabled,
      voiceName: (f.voiceName || '').trim(),
      knowledgeFilesEnabled: f.knowledgeFilesEnabled,
      documentInputEnabled: f.documentInputEnabled,
    };

    if (this.isNew) {
      const projectId = blankToNull(f.projectId);
      if (!projectId) {
        this.error = 'Project is required.';
        return;
      }
      const body: AgentRequest = {
        projectId,
        name,
        avatarUrl: blankToNull(f.avatarUrl),
        modelId,
        temperature,
        maxOutputTokens: numOrNull(f.maxOutputTokens),
        historyLimit: numOrNull(f.historyLimit),
        status: f.status,
        welcomeMessage: blankToNull(f.welcomeMessage),
        fallbackMessage: blankToNull(f.fallbackMessage),
        identity: this.cleanIdentity(),
        reasoningEffort: this.thinkingLevels.length ? f.reasoningEffort || '' : null,
        ...features,
        replyDelayMs: features.replyDelayMs ?? REPLY_DELAY_DEFAULT_MS,
        voiceName: features.voiceName || null,
      };
      this.api.createAgent(body).subscribe({
        next: (created) => this.router.navigate(['/ai-agents/agents', created.id], { queryParams: { tab: 'channels' } }),
        error: (err) => (this.error = agentSaveErrorMessage(err, 'Creating the agent failed.')),
      });
      return;
    }

    // Update: null keeps a value, so text fields are sent as strings ('' clears them). The picture URL is sent only
    // when it was edited here, so a save never overwrites a picture the customer uploaded meanwhile.
    const avatarUrl = (f.avatarUrl || '').trim();
    const body: AgentRequest = {
      name,
      avatarUrl: avatarUrl === (this.agent?.avatarUrl || '') ? null : avatarUrl,
      reasoningEffort: this.thinkingLevels.length ? f.reasoningEffort || '' : null,
      modelId,
      temperature,
      maxOutputTokens: numOrNull(f.maxOutputTokens),
      historyLimit: numOrNull(f.historyLimit),
      status: f.status,
      welcomeMessage: f.welcomeMessage ?? '',
      fallbackMessage: f.fallbackMessage ?? '',
      ...features,
    };
    this.api.updateAgent(this.agentId as string, body).subscribe({
      next: (updated) => {
        this.agent = updated;
        this.form = this.toForm(updated);
        this.savedNote = 'Settings saved.';
      },
      error: (err) => (this.error = agentSaveErrorMessage(err, 'Saving failed.')),
    });
  }

  saveIdentity(): void {
    this.identityError = '';
    this.savedNote = '';
    if (this.isNew) {
      // On a new agent the identity is sent with the create call.
      this.saveSettings();
      return;
    }
    this.api.putIdentity(this.agentId as string, this.cleanIdentity()).subscribe({
      next: (saved) => {
        this.identity = { ...emptyIdentity(), ...saved };
        this.savedNote = 'Identity saved.';
      },
      error: (err) => (this.identityError = err?.error?.message || 'Saving the identity failed.'),
    });
  }

  identityLength(key: keyof AgentIdentity): number {
    return (this.identity[key] || '').length;
  }

  private cleanIdentity(): AgentIdentity {
    const out = emptyIdentity();
    for (const s of this.sections) {
      const v = this.identity[s.key];
      out[s.key] = v === null || v === undefined || v.trim() === '' ? null : v;
    }
    return out;
  }

  private toForm(a: Agent): SettingsForm {
    return {
      projectId: a.projectId,
      name: a.name || '',
      avatarUrl: a.avatarUrl || '',
      modelId: a.modelId || '',
      temperature: a.temperature,
      maxOutputTokens: a.maxOutputTokens,
      historyLimit: a.historyLimit,
      status: a.status,
      welcomeMessage: a.welcomeMessage || '',
      fallbackMessage: a.fallbackMessage || '',
      reasoningEffort: a.reasoningEffort || '',
      replyDelayMs: a.replyDelayMs ?? REPLY_DELAY_DEFAULT_MS,
      imageInputEnabled: a.imageInputEnabled === true,
      voiceInputEnabled: a.voiceInputEnabled === true,
      voiceOutputEnabled: a.voiceOutputEnabled === true,
      voiceName: a.voiceName || '',
      knowledgeFilesEnabled: a.knowledgeFilesEnabled === true,
      documentInputEnabled: a.documentInputEnabled === true,
    };
  }

  private blankForm(): SettingsForm {
    return {
      projectId: '',
      name: '',
      avatarUrl: '',
      modelId: '',
      temperature: null,
      maxOutputTokens: null,
      historyLimit: 20,
      status: 'DRAFT',
      welcomeMessage: '',
      fallbackMessage: '',
      reasoningEffort: '',
      replyDelayMs: REPLY_DELAY_DEFAULT_MS,
      imageInputEnabled: false,
      voiceInputEnabled: false,
      voiceOutputEnabled: false,
      voiceName: '',
      knowledgeFilesEnabled: false,
      documentInputEnabled: false,
    };
  }
}
